import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isAxiosError } from 'src/api/instance';
import { actions as myWorkActions } from 'src/features/myWork/myWorkSlice';
import useSnackbarProvider from 'src/hooks/useSnackbarProvider';
import api from '../api';
import { logReviewSubmitFailed, VoaSignalContext } from '../observability';
import { clearStoredState, CompleteReviewPayload } from '../utils';

const CONFLICT_MESSAGE =
  'This review was already completed or reassigned. It is still assigned to you — refresh and try again.';
const VALIDATION_MESSAGE =
  'The review could not be submitted because some data is invalid. Fix the highlighted fields and try again.';
const GENERIC_MESSAGE = 'An error has occurred, please refresh and try again';

export const useCompleteAssetReview = (storageKey: string) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { SnackTypes, showSnackbar, VariantType } = useSnackbarProvider();
  const inFlightRef = useRef(false);

  const onCompleteReview = useCallback(
    async (entryId: string, payload: CompleteReviewPayload, signalContext: VoaSignalContext) => {
      if (inFlightRef.current) return false;
      inFlightRef.current = true;
      try {
        await api.completeReview(entryId, payload);
        showSnackbar(VariantType.success, 'The review has been submitted', SnackTypes.none);
        clearStoredState(storageKey);
        // plan-review M1: myWorkSlice only auto-clears on the IV/fraud thunks; without
        // this dispatch My Work keeps offering a resume link to a completed review.
        dispatch(myWorkActions.setAssignedEntry(null));
        navigate('/my-work');
        return true;
      } catch (error) {
        // Failure keeps the item assigned (AC6): no myWork dispatch, no storage clear,
        // no navigation — every entered value stays intact for the retry.
        const failureDetail = {
          account_count: payload.accounts.length,
          statement_count: payload.accounts.reduce((sum, a) => sum + a.statements.length, 0)
        };
        if (isAxiosError(error) && error.response?.status === 409) {
          showSnackbar(VariantType.error, CONFLICT_MESSAGE, SnackTypes.none);
          logReviewSubmitFailed(signalContext, {
            ...failureDetail,
            error_type: 'conflict',
            http_status: 409
          });
        } else if (isAxiosError(error) && error.response?.status === 400) {
          showSnackbar(VariantType.error, VALIDATION_MESSAGE, SnackTypes.none);
          logReviewSubmitFailed(signalContext, {
            ...failureDetail,
            error_type: 'validation_error',
            http_status: 400
          });
        } else {
          showSnackbar(VariantType.error, GENERIC_MESSAGE, SnackTypes.none);
          logReviewSubmitFailed(signalContext, {
            ...failureDetail,
            error_type: isAxiosError(error) && !error.response ? 'network_error' : 'unknown_error',
            http_status: isAxiosError(error) ? error.response?.status : undefined
          });
        }
        return false;
      } finally {
        inFlightRef.current = false;
      }
    },
    [dispatch, navigate, showSnackbar, SnackTypes.none, VariantType, storageKey]
  );

  return { onCompleteReview };
};

export default useCompleteAssetReview;
